/**
 * Rounding helpers that take no imports.
 *
 * They live apart from [`Utils.ts`](Utils.ts) (which holds `roundWithPrecision`)
 * because that module reaches Firestore and the store, so a unit test cannot import
 * it. The rounding rule these encode is worth testing directly, so it belongs in a
 * leaf — the same reason [`LanguageTypes.ts`](../i18n/LanguageTypes.ts) exists.
 */

/**
 * Rounds a value to one decimal as usual (half up), then **up** to a whole number:
 * `5.02 → 5.0 → 5`, `5.08 → 5.1 → 6`. This is the rule for the step-9 drying areas,
 * which the HyDry certificate reports in full m² (Hans, 2026-09-29). The first step
 * keeps a sliver such as `5.02` from costing a whole m²; the second keeps a real
 * fraction from being under-stated.
 *
 * @param value value to round
 * @returns the whole number the value rounds to
 */
export function ceilFromOneDecimal(value: number): number {
  // Trim binary noise before rounding to the nearest tenth: a sum such as
  // 8.04 + 0.01 comes out as 8.049999999999999, and it has to round as the 8.05 it
  // is (to 8.1, so 9), not to 8.0.
  const tenths = Math.round(Number((value * 10).toPrecision(12)));

  return Math.ceil(tenths / 10);
}
